import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { Button } from '@mui/material'
import { buttonClasses } from '@mui/material/Button'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  CREDITS_URL,
  GIFT_CARD_URL,
  PROFILE_ADDRESSES_URL,
  PROFILE_CHANGE_INFO_URL,
  PROFILE_DISCOUNT_URL,
  PROFILE_FAVORITES_URL,
  PROFILE_ORDER_HISTORY_URL,
  PROFILE_PATTERN_PACKS_URL,
  PROFILE_REWARDS_URL,
  PROFILE_SUBSCRIPTIONS_URL,
} from '../../../urls'
import WholesaleMode from '@components/WholesaleMode'
import { useMeQuery } from '@graphql/queries/__generated__/me.generated'
import { useApolloClient } from '@apollo/client'
import { useLogoutMutation } from '@graphql/mutations/__generated__/auth.generated'
import { showPrice } from '../../../misc/price'
import Loader from '@components/Loader'

const useStyles = makeStyles(theme => ({
  headerButton: {
    [`&.${buttonClasses.root}`]: {
      justifyContent: 'space-between',
      height: 50,
      padding: 0,
      fontWeight: 700,
      fontSize: 16,
      textTransform: 'initial',
      width: '100%',
      borderBottom: '1px solid #d8d8d8',
    },
  },
  button: {
    [`&.${buttonClasses.root}`]: {
      justifyContent: 'space-between',
      height: 50,
      padding: '0 0 0 24px',
      fontWeight: 700,
      fontSize: 16,
      textTransform: 'initial',
      width: '100%',
    },
  },
  subCategoryButton: {
    [`&.${buttonClasses.root}`]: {
      padding: '0 0 0 24px',
      fontSize: 14,
      textTransform: 'initial',
      width: '100%',
      // fontWeight: 700,
      justifyContent: 'space-between',
      height: 36,
    },
  },
}))

interface IProps {
  goBack(): void

  onClickLink(e: any): void
}

const MobileMenuAccountLvl2: React.FC<IProps> = ({ onClickLink, goBack }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const client = useApolloClient()
  const [logout] = useLogoutMutation()
  const [isLoading, setIsLoading] = React.useState(false)
  const { data: dataMe, loading } = useMeQuery()

  const onLogout = async e => {
    e.preventDefault()
    setIsLoading(true)
    try {
      await logout()
      await client.resetStore()
    } catch (e) {
      console.log(e.message)
    }
    setIsLoading(false)
  }

  return (
    <div className='pb-20'>
      <Loader
        className='spinner'
        type='ThreeDots'
        color='#333'
        visible={isLoading || loading}
        height={100}
        width={100}
      />
      <Button className={classes.headerButton} onClick={goBack}>
        <ChevronLeftIcon fontSize='large' className='ml-10' />
        <span className='absolute w-full text-lg'>
          <span className='text-center px-30 block leading-normal'>{t('myAccount', 'My Account')}</span>
        </span>
      </Button>
      <ul className='absolute h-full w-full overflow-auto list-none pl-0 pb-110'>
        {dataMe?.me && (
          <WholesaleMode hide>
            <li className='mb-5'>
              <Button component={Link} className={classes.button} to={PROFILE_REWARDS_URL} onClick={onClickLink}>
                <span className='text-left'>
                  {t('menu.rewards', {
                    defaultValue: 'My Rewards ({{totalBalance}} out of {{rpThreshold}})',
                    totalBalance: dataMe.me.totalPointsBalanceAvailable,
                    rpThreshold: 150,
                  })}
                </span>
              </Button>
            </li>
          </WholesaleMode>
        )}
        <li className='mb-5'>
          <Button component={Link} className={classes.button} to={PROFILE_DISCOUNT_URL} onClick={onClickLink}>
            <span className='text-left'>{t('menu.myDiscount', 'My Discount')}</span>
          </Button>
        </li>
        <WholesaleMode hide>
          <li className='mb-5'>
            <Button component={Link} className={classes.button} to={CREDITS_URL} onClick={onClickLink}>
              <span className='text-left'>
                {t('menu.credits', {
                  defaultValue: `My Credits ({{balance}})`,
                  balance: showPrice({
                    amount: +dataMe?.me?.totalBalance?.gross || 0,
                    currency: dataMe?.me?.totalBalance?.currency || 'USD',
                  }),
                })}
              </span>
            </Button>
          </li>
          <li className='mb-5'>
            <Button component={Link} className={classes.button} to={PROFILE_PATTERN_PACKS_URL} onClick={onClickLink}>
              <span className='text-left'>{t('menu.patternPacks', 'Pattern Packs')}</span>
            </Button>
          </li>
          <li className='mb-5'>
            <Button component={Link} className={classes.button} to={GIFT_CARD_URL} onClick={onClickLink}>
              <span className='text-left'>{t('menu.giftCard', 'Gift Cards')}</span>
            </Button>
          </li>
        </WholesaleMode>
        <li className='mb-5'>
          <Button component={Link} className={classes.button} to={PROFILE_ORDER_HISTORY_URL} onClick={onClickLink}>
            <span className='text-left'>{t('menu.orderHistory', 'Order History')}</span>
          </Button>
        </li>
        <li className='mb-5'>
          <Button component={Link} className={classes.button} to={PROFILE_FAVORITES_URL} onClick={onClickLink}>
            <span className='text-left'>{t('menu.favorites', 'Favorites')}</span>
          </Button>
        </li>
        <li className='mb-5'>
          <Button component={Link} className={classes.button} to={PROFILE_CHANGE_INFO_URL} onClick={onClickLink}>
            <span className='text-left'>{t('menu.accountDetails', 'Account Details')}</span>
          </Button>
        </li>
        <li className='mb-5'>
          <Button component={Link} className={classes.button} to={PROFILE_ADDRESSES_URL} onClick={onClickLink}>
            <span className='text-left'>{t('menu.addressBook', 'Address Book')}</span>
          </Button>
        </li>
        <li className='mb-5'>
          <Button component={Link} className={classes.button} to={PROFILE_SUBSCRIPTIONS_URL} onClick={onClickLink}>
            <span className='text-left'>{t('menu.contactPreferences', 'Contact Preferences')}</span>
          </Button>
        </li>
        <li className='mb-5'>
          <Button
            className={classes.button}
            onClick={e => {
              onLogout(e)
              goBack()
              onClickLink(e)
            }}
          >
            <span className='text-left'>{t('menu.signOut', 'Sign Out')}</span>
          </Button>
        </li>
      </ul>
    </div>
  )
}

export default MobileMenuAccountLvl2
